import {
	BooleanOperators,
	CurrencyOperators,
	DateTimeOperators,
	NumericOperators,
	Operators,
	TextOperators,
} from '../types/operators.types';
import {
	BooleanPropertyType,
	DateTimePropertyType,
	GenericSinglePropertyType,
	isBooleanProperty,
	isCurrencyProperty,
	isDateTimeProperty,
	isNOBProperty,
	isNumericProperty,
	isTextProperty,
	NOBPropertyType,
	NumericPropertyType,
	TextPropertyType,
} from '../types/property.types';

/**
 * Get the operators for a given property type with optional context
 * @internal
 * @param type - The property type
 * @param context - Optional context containing unitsCode metadata and property ID
 * @returns The appropriate operators (currency for WN_COST unitsCode, otherwise based on type)
 */
export function getOperatorsForType(
	type: GenericSinglePropertyType,
	context?: { unitsCode?: string; subType?: string },
): Operators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The text property type
 * @returns The text operators
 */
export function getOperatorsForType(type: TextPropertyType): TextOperators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The numeric property type
 * @returns The numeric operators
 */
export function getOperatorsForType(
	type: NumericPropertyType,
): NumericOperators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The boolean property type
 * @returns The boolean operators
 */
export function getOperatorsForType(
	type: BooleanPropertyType,
): BooleanOperators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The date/time property type
 * @returns The date/time operators
 */
export function getOperatorsForType(
	type: DateTimePropertyType,
	context?: { unitsCode: undefined; subType?: 'Year' },
): DateTimeOperators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The NOB property type
 * @returns The text operators (NOB properties use text-like operators)
 */
export function getOperatorsForType(type: NOBPropertyType): TextOperators;
/**
 * Get the operators for a given property type
 * @internal
 * @param type - The type of property to get operators for
 * @returns The operators for the given property type
 */
export function getOperatorsForType(type: GenericSinglePropertyType): Operators;
/**
 * Implementation of getOperatorsForType
 * @internal
 * @param type - The type of property to get operators for
 * @param context - Optional context containing unitsCode metadata and property ID
 * @returns The operators for the given property type
 */
export function getOperatorsForType(
	type: GenericSinglePropertyType,
	context?: { unitsCode?: string; subType?: string },
): Operators {
	if (context && isCurrencyProperty(context)) {
		return new CurrencyOperators();
	} else if (isTextProperty(type)) {
		return new TextOperators(context?.subType);
	} else if (isNumericProperty(type)) {
		return new NumericOperators();
	} else if (isBooleanProperty(type)) {
		return new BooleanOperators();
	} else if (isDateTimeProperty(type)) {
		return new DateTimeOperators(context?.subType);
	} else if (isNOBProperty(type)) {
		return new TextOperators();
	}

	throw new Error(`Unknown property type: ${type}`);
}
